import { useCallback } from 'react';
import { useLang } from 'hooks/lang';

import { loaders } from 'entities';

import { Column } from 'components/Blocks';

import { SingleDiscountRequest } from './Request';
import { TriStateWrapper } from '../components';

import { usePendingActions } from 'providers/PendingActions';
import { useUser } from 'hooks/user';

import { listRemove, sortByProperty } from 'utils/array';

export function DiscountRequest() {
  const { t } = useLang();
  const user = useUser();

  const { decreaseCount } = usePendingActions();

  const {
    data: requests,
    loading,
    overwrite,
  } = loaders.invoice.activeProjectInvoiceDiscountRequests.use({
    defaultValue: [],

    parser: (discounts) =>
      sortByProperty(discounts, ['salesPerson', 'nonLeaderApproval', 'requestedAt'], {
        salesPerson: (salesA, salesB) => {
          /*
          We take advantage of boolean math here.

            1. true = 1, false = 0
            2. we want all requests from logged in user to be first
            3. from user = true, not = false
            4. When we sort a numeric array descending, we do B - A, and all the bigger
            numbers end up in front => 1 > 0!
        */
          const [aNumber, bNumber] = [salesA.id, salesB.id].map((id) => Number(user.id === id));

          return bNumber - aNumber;
        },

        // here we do the opposite than above: non leaders that approved requests will have them show
        // up last
        nonLeaderApproval: (approved1, approved2) => Number(approved1) - Number(approved2),

        // oldest first
        requestedAt: (dateA, dateB) => Number(new Date(dateA)) - Number(new Date(dateB)),
      }),
  });

  const removeRequest = useCallback(
    ({ approved, comment, id }: { approved: boolean; comment: string; id: string }) => {
      if (user.type === 'LEADER' || !approved) {
        overwrite((old) => listRemove(old, id));

        return decreaseCount('ACTIVE_DISCOUNT_REQUESTS');
      }

      overwrite((old) =>
        old.map((request) =>
          request.id !== id
            ? request
            : { ...request, nonLeaderApproval: true, requestComment: comment },
        ),
      );
    },
    [overwrite, user.type, decreaseCount],
  );

  return (
    <TriStateWrapper
      loading={loading}
      message={!requests.length && t('no-discount-request-right-now')}
    >
      <Column className="mx-auto w-[min(80%,850px)] phone:w-full">
        {requests.map((request) => (
          <SingleDiscountRequest key={request.id} request={request} updateRequest={removeRequest} />
        ))}
      </Column>
    </TriStateWrapper>
  );
}
